import type { CallEndpoint, Participant } from '@wtfalch/calls/browser';
import { createCallEndpoint } from '@wtfalch/calls/browser';
import { Button, Icon } from '@wtfalch/design';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { CallGrid } from './CallGrid.tsx';
import { GUEST_NAME_MAX, guestDisplayName } from './guestName.ts';
import { memoryStore } from './memoryStore.ts';

/** What a guest link carries: 32 random bytes, base64url without padding. */
const SECRET = /^[A-Za-z0-9_-]{43}$/;

/** How long a guest waits at the door before the wait gives up. */
const ADMISSION_MS = 5 * 60_000;

/** The secret in the address, or null. It is the fragment, which no request
 *  ever carries, and it is read once. */
export function readGuestSecret(hash: string): string | null {
  let value: string;
  try {
    value = decodeURIComponent(hash.replace(/^#/, ''));
  } catch (error) {
    console.error('The guest link is not valid text:', error instanceof Error ? error.name : error);
    return null;
  }
  return SECRET.test(value) ? value : null;
}

/** Guest browsers on phones are not supported yet. */
export function isPhone(
  nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'>,
  width: number,
): boolean {
  return (
    /Android.+Mobile|iPhone|iPod/i.test(nav.userAgent) || (nav.maxTouchPoints > 0 && width < 600)
  );
}

async function defaultEndpoint(): Promise<CallEndpoint> {
  const { createLiveKitAdapter } = await import('@wtfalch/calls/livekit');
  return createCallEndpoint({
    adapter: createLiveKitAdapter({}),
    store: memoryStore(),
    baseUrl: `${window.location.origin}/_calls`,
  });
}

export type Phase =
  | { kind: 'name' }
  | { kind: 'redeeming' }
  | { kind: 'waiting' }
  | { kind: 'joining' }
  | { kind: 'in'; sessionId: string }
  | { kind: 'over'; message: string };

type Failure = { code?: unknown; reason?: unknown; message?: unknown };

/** The words for whatever the redeem or the wait answered. The engine throws
 *  plain errors carrying the service's `code` and `reason`. */
export function describeGuestFailure(error: unknown, waiting: boolean): string {
  const failure = (error ?? {}) as Failure;
  if (waiting) {
    return failure.message === 'admission_pending'
      ? 'Nobody let you in. Open the link again to ask once more.'
      : 'You were not let in.';
  }
  if (failure.reason === 'capacity_reached') return 'This call is full.';
  if (failure.reason === 'room_not_open') return 'This call is not open yet.';
  if (failure.code === 'rate_limited') return 'Too many tries. Wait a minute and try again.';
  if (
    failure.code === 'not_found' ||
    failure.code === 'forbidden' ||
    failure.code === 'unauthorized'
  ) {
    return 'This link has expired or was revoked. Ask whoever invited you for a new one.';
  }
  return 'Could not join the call. Try again.';
}

const ENDED: Record<string, string> = {
  left: 'You left the call.',
  session_ended: 'The call has ended.',
  participant_removed: 'You were removed from the call.',
  admission_lapsed: 'Your place in the call ended.',
};

/**
 * The page a guest opens from a link, outside company sign-in. It asks for a
 * name, waits to be let in, then draws the call: audio, video and screen share
 * and nothing else, with no chat, mail or company data loaded. It never reads
 * the member session and never sends the member bearer: the engine is made
 * without a token. The secret leaves the address bar as soon as it is read.
 */
export function GuestCall({
  makeEndpoint = defaultEndpoint,
  nav = navigator,
  width = window.innerWidth,
  hash = window.location.hash,
}: {
  makeEndpoint?: () => Promise<CallEndpoint> | CallEndpoint;
  nav?: Pick<Navigator, 'userAgent' | 'maxTouchPoints'>;
  width?: number;
  /** The address fragment. `/__preview` passes a made-up one. */
  hash?: string;
}) {
  const [secret] = useState(() => readGuestSecret(hash));
  const [name, setName] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'name' });
  const [people, setPeople] = useState<Participant[]>([]);
  const endpointRef = useRef<CallEndpoint | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  useEffect(
    () => () => {
      const endpoint = endpointRef.current;
      endpointRef.current = null;
      endpoint?.leave().catch((error: unknown) => console.error('Could not stop the call:', error));
    },
    [],
  );

  if (isPhone(nav, width)) {
    return (
      <Page>
        <h1 className="h1 m-0">Open this on a computer</h1>
        <p className="m-0 text-muted">
          Calls for guests do not work in a phone's browser yet. Open the link on a computer.
        </p>
      </Page>
    );
  }

  if (!secret) {
    return (
      <Page>
        <h1 className="h1 m-0">This link is not valid</h1>
        <p className="m-0 text-muted">Ask whoever invited you for a new one.</p>
      </Page>
    );
  }

  const join = async (event: FormEvent) => {
    event.preventDefault();
    const displayName = guestDisplayName(name, '');
    if (!displayName || startedRef.current) return;
    startedRef.current = true;
    setPhase({ kind: 'redeeming' });
    let endpoint: CallEndpoint;
    let waiting = false;
    try {
      endpoint = await makeEndpoint();
      endpointRef.current = endpoint;
      const redeemed = await endpoint.redeem({ secret, displayName });
      if (redeemed.state !== 'admitted') {
        waiting = true;
        setPhase({ kind: 'waiting' });
        await endpoint.awaitAdmission(ADMISSION_MS);
      }
      setPhase({ kind: 'joining' });
      endpoint.subscribe((stats) => {
        setPeople(stats.participants);
        const reason = stats.ended?.reason;
        if (reason) setPhase({ kind: 'over', message: ENDED[reason] ?? 'The call ended.' });
      });
      await endpoint.join({ sessionId: redeemed.sessionId, mode: 'audio' });
      setPhase((current) =>
        current.kind === 'over' ? current : { kind: 'in', sessionId: redeemed.sessionId },
      );
    } catch (error) {
      console.error('Guest join failed:', (error as Failure)?.code ?? 'unknown');
      setPhase({ kind: 'over', message: describeGuestFailure(error, waiting) });
    }
  };

  if (phase.kind === 'in') {
    const live = endpointRef.current;
    if (!live) {
      return (
        <GuestScreen phase={{ kind: 'joining' }} name={name} onName={setName} onSubmit={join} />
      );
    }
    return (
      <div className="flex flex-col h-screen">
        <CallGrid
          endpoint={live}
          sessionId={phase.sessionId}
          participants={people}
          heading="Call"
          onLeft={() => setPhase({ kind: 'over', message: 'You left the call.' })}
        />
      </div>
    );
  }

  return <GuestScreen phase={phase} name={name} onName={setName} onSubmit={join} />;
}

/** Every screen a guest sees apart from the call itself. */
export function GuestScreen({
  phase,
  name,
  onName,
  onSubmit,
}: {
  phase: Exclude<Phase, { kind: 'in' }>;
  name: string;
  onName: (name: string) => void;
  onSubmit: (event: FormEvent) => void;
}) {
  if (phase.kind === 'over') {
    return (
      <Page>
        <h1 className="h1 m-0">{phase.message}</h1>
      </Page>
    );
  }

  if (phase.kind === 'name') {
    return (
      <Page>
        <h1 className="h1 m-0">Join the call</h1>
        <form className="flex flex-col gap-3 w-full max-w-80" onSubmit={onSubmit}>
          <label className="flex flex-col gap-1 text-sm text-left">
            Your name
            <input
              className="p-2 rounded-md border border-border bg-transparent"
              value={name}
              maxLength={GUEST_NAME_MAX}
              autoComplete="name"
              onChange={(event) => onName(event.target.value)}
            />
          </label>
          <Button kind="primary" type="submit" disabled={guestDisplayName(name, '') === ''}>
            Ask to join
          </Button>
        </form>
      </Page>
    );
  }

  return (
    <Page>
      <p className="flex items-center gap-2 m-0" role="status">
        <Icon name="spinner" size="md" />
        {phase.kind === 'waiting' ? 'Waiting for someone to let you in' : 'Joining the call…'}
      </p>
    </Page>
  );
}

function Page({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex flex-col items-center justify-center gap-5 min-h-screen p-8 text-center">
      {children}
    </main>
  );
}
